import { startCase } from 'lodash';

import { inHouseWinnerReason } from 'components/inboundReferrals/notSelectedReason';
import { Dimensions, Metrics, Sources, WITH_ID_SUFFIX } from 'components/insights/constants';
import { InsightsQueryParams } from 'components/insights/query/useInsightsAnalytics';
import { colors } from 'styles/theme/colors';

export const INBOUND_VIEW_BY = {
  RECEIVING_SNF: 'receivingSnf',
  REFERRAL_SOURCE: 'referralSource',
  PAYER_SOURCE: 'payerSource',
} as const;

export type InboundViewByValue = (typeof INBOUND_VIEW_BY)[keyof typeof INBOUND_VIEW_BY];

export const INBOUND_VIEW_BY_DIMENSION_MAP: Record<InboundViewByValue, Dimensions> = {
  receivingSnf: Dimensions.FACILITY,
  referralSource: Dimensions.REFERRAL_SOURCE,
  payerSource: Dimensions.PAYER_SOURCE,
};

export const INBOUND_VIEW_BY_ENTITY_HEADER: Record<InboundViewByValue, string> = {
  receivingSnf: 'Receiving SNF',
  referralSource: 'Referral Source',
  payerSource: 'Primary Payer',
};

// Donut slices for the Inbound Referral Highlights chart — the determination funnel: every referral
// building is Accepted, Denied, or Pending. Selected / Not Selected are a separate lens shown as
// rate tiles, not donut slices, because a selected or not-selected building can come from any of
// the determination buckets (a facility can be selected whether its own decision was pending,
// accepted, or denied), so they'd double-count against a single pie total.
export const INBOUND_STATUS = {
  ACCEPTED: 'accepted',
  DENIED: 'denied',
  PENDING: 'pending',
} as const;

export type InboundStatus = (typeof INBOUND_STATUS)[keyof typeof INBOUND_STATUS];

export const INBOUND_STATUS_LABELS: Record<InboundStatus, string> = {
  accepted: 'Accepted',
  denied: 'Denied',
  pending: 'Pending Decision',
};

export const INBOUND_STATUS_COLORS: Record<InboundStatus, string> = {
  accepted: colors.green100,
  denied: colors.red100,
  pending: colors.yellow100,
};

export const INBOUND_STATUSES = Object.keys(INBOUND_STATUS_LABELS) as InboundStatus[];

// Denial-reason categories (the Denied drill's donut, OLIO-2897). Fixed cross-client rollup —
// WorkflowActionDefinition::FacilityDecision::DENIAL_CATEGORIES on the API. Order drives the donut
// slices and legend. The specific reasons within a category (denial_reason_description) are
// client-configurable slugs; we humanize the slug rather than resolve the client's lookup label,
// so a few acronyms read plainly (e.g. "Tbi", "Trach New").
export const DENIAL_CATEGORY_ORDER = ['clinical', 'financial', 'other'] as const;
export type DenialCategory = (typeof DENIAL_CATEGORY_ORDER)[number];

export const DENIAL_CATEGORY_LABELS: Record<DenialCategory, string> = {
  clinical: 'Clinical',
  financial: 'Financial',
  other: 'Other',
};

export const DENIAL_CATEGORY_COLORS: Record<DenialCategory, string> = {
  clinical: colors.red100,
  financial: colors.red50,
  other: colors.black25,
};

export function denialCategoryLabel(category: string): string {
  return DENIAL_CATEGORY_LABELS[category as DenialCategory] ?? startCase(category);
}

// A specific denial reason, prefixed with its category — "Clinical - Trach - New". The description
// arrives from the API already resolved to its display name (client lookup, slug fallback), so it's
// used as-is. Used for the Top Denial Reason tile and the expandable breakdown rows.
export function denialReasonLabel(category: string | null, description: string): string {
  return category ? `${denialCategoryLabel(category)} - ${description}` : description;
}

// Synthetic reason for not-selected losers carrying no run-level reason. Kept as a real
// bucket so the reason volumes sum to the total Not Selected count. Coupled by convention to the
// same slug the breakdown side query assigns to the NULL-reason bucket.
//
// WHAT IS ACTUALLY IN IT: buildings on a referral one of the CLIENT'S OWN buildings won.
// AcceptReferral#drop_contradictions! nils `not_selected_reason` on a Selected outcome and nils
// `selected_facility_id` on a Not Selected one, so a run can never carry both, which is why a
// not-selected building with no reason means an in-house winner rather than a missing answer. A lost
// referral is never in here: `not_selected_reason` is required and validated, so it is never null.
//
// A building that DENIED an otherwise-won referral is in here too: `facility_outcomes_for` projects
// every non-winner as not_selected whatever its own determination (Attributes::SelectionMetric: "read
// independently of the building's own determination"). That is intended. The sentence describes the
// referral, and the Referral Outcome card says it to a denied building as well. Only the ALL-denied
// referral escapes, via the `facility_outcomes => nil` tombstone record_denied_by_every_building!
// writes (product call 2026-09-03).
export const CHOSE_DIFFERENT_FACILITY = 'chose_different_facility';

// The default (global) not_selected_reasons lookup, mirrored by convention from the API's canonical
// list (api :: app/seeds/seed_data/workflow_lookups.rb :: NOT_SELECTED_REASONS). A client can
// nominate its own list, but the breakdown chart reserves its loading height off this default count
// (plus the synthetic in-house-winner bucket above) so the placeholder tracks the real default
// rather than a magic number. Keep in sync with the backend default.
export const DEFAULT_NOT_SELECTED_REASONS = ['chose_competitor', 'different_level_of_care', 'deceased', 'other'];

// A not-selected reason label. Real reasons arrive from the API already resolved to their display
// name (client lookup, slug fallback), so they're used as-is; the synthetic NULL-reason bucket
// (passed as CHOSE_DIFFERENT_FACILITY) gets a human label built here.
//
// It NAMES THE CLIENT because "Patient chose different facility" read as though the
// patient went to a competitor when in fact they went to another of this client's own buildings:
// the opposite result commercially, and the one thing this row exists to report. The name is the
// viewer's own organisation: this is an aggregate across referrals, so there is no per-referral
// client to resolve, and a viewer only ever sees their own client's referrals.
//
// Falls back to a name-free wording rather than interpolating an empty string, so a profile that
// has not resolved yet reads as vague instead of as "another  facility".
export function notSelectedReasonLabel(value: string, clientName?: string | null): string {
  if (value !== CHOSE_DIFFERENT_FACILITY) return value;

  return inHouseWinnerReason(clientName);
}

// Metrics requested for the highlights rollup + details table rows. Counts come from the API
// (row grain, same denominator as the rates) so the funnel/donut/subtitles use real integers
// instead of reconstructing them from rounded rates.
export const INBOUND_DASHBOARD_METRICS: Metrics[] = [
  Metrics.REFERRAL_COUNT,
  Metrics.ACCEPTANCE_RATE,
  Metrics.DENIAL_RATE,
  Metrics.PENDING_RATE,
  Metrics.SELECTED_RATE,
  Metrics.NOT_SELECTED_RATE,
  Metrics.ACCEPTED_COUNT,
  Metrics.DENIED_COUNT,
  Metrics.PENDING_COUNT,
  Metrics.SELECTED_COUNT,
  Metrics.NOT_SELECTED_COUNT,
  // The "total closed" denominator read straight from the API (selected + not_selected), so the
  // landing tiles and the drill headers show the same number under the same wording.
  Metrics.CLOSED_COUNT,
  Metrics.ADMITTED_COUNT,
  Metrics.ADMISSION_RATE,
  Metrics.AWAITING_ADMISSION_COUNT,
  Metrics.TIME_TO_FACILITY_DECISION_SECONDS_AVERAGE,
];

// The detail table renders total received, the three determination rates, and avg decision time.
// Export mirrors those columns — not the highlight-only selected/not-selected rates or the raw
// counts, which no table column shows.
export const INBOUND_TABLE_METRICS: Metrics[] = [
  Metrics.REFERRAL_COUNT,
  Metrics.ACCEPTANCE_RATE,
  Metrics.DENIAL_RATE,
  Metrics.PENDING_RATE,
  Metrics.TIME_TO_FACILITY_DECISION_SECONDS_AVERAGE,
];

export function getInboundDetailsParams(dimension: Dimensions): InsightsQueryParams {
  return {
    source: Sources.REFERRAL_INTAKES,
    dimensions: [dimension],
    metrics: INBOUND_DASHBOARD_METRICS,
    rollups: true,
  };
}

// The entity-drill slideout header shows the three determination rates + avg decision time (with
// accepted/denied/pending counts for the totals); its breakdown table shows received + the rates +
// avg time. It does NOT show the selected/not-selected rates or counts, so the slideout query omits
// them (unlike the landing highlights card, which does).
export const INBOUND_ENTITY_SLIDEOUT_METRICS: Metrics[] = [
  Metrics.REFERRAL_COUNT,
  Metrics.ACCEPTANCE_RATE,
  Metrics.DENIAL_RATE,
  Metrics.PENDING_RATE,
  Metrics.ACCEPTED_COUNT,
  Metrics.DENIED_COUNT,
  Metrics.PENDING_COUNT,
  Metrics.TIME_TO_FACILITY_DECISION_SECONDS_AVERAGE,
];

export function getInboundEntitySlideoutParams(dimension: Dimensions): InsightsQueryParams {
  return {
    source: Sources.REFERRAL_INTAKES,
    dimensions: [dimension],
    metrics: INBOUND_ENTITY_SLIDEOUT_METRICS,
    rollups: true,
  };
}

// A built metric drill's key. Locked to the titles map (same source the store keys off) so a
// typo'd drill key fails to compile instead of silently hitting the fallback below.
type InboundMetricDrillKey = keyof typeof INBOUND_METRIC_DRILL_TITLES;

// Metrics each metric-drill needs for its header tiles, chart, and breakdown table (Accepted,
// Denied, Selected, Not Selected, and Admitted). FACILITY_COUNT rides on every set for the
// "Referrals to N SNFs" subtitle (read off the overall rollup) — omit it and the subtitle silently
// reads 0.
export const INBOUND_METRIC_DRILL_METRICS: Partial<Record<InboundMetricDrillKey, Metrics[]>> = {
  accepted: [
    Metrics.REFERRAL_COUNT,
    Metrics.ACCEPTANCE_RATE,
    Metrics.ACCEPTED_COUNT,
    Metrics.TIME_TO_ACCEPTED_DECISION_SECONDS_AVERAGE,
    // Distinct receiving-SNF count for the "Referrals to N SNFs" subtitle (metric drills only),
    // read off the overall rollup. COUNT(DISTINCT groups.name), matching the facility breakdown grain.
    Metrics.FACILITY_COUNT,
  ],
  denied: [
    Metrics.REFERRAL_COUNT,
    Metrics.DENIAL_RATE,
    Metrics.DENIED_COUNT,
    Metrics.TIME_TO_DENIAL_DECISION_SECONDS_AVERAGE,
    Metrics.FACILITY_COUNT,
  ],
  selected: [
    Metrics.SELECTED_COUNT, // blue "Total Selected" bar + table column
    Metrics.SELECTED_RATE,
    // closed_count = buildings that reached a recorded outcome (selected + not_selected) — the grey
    // chart base, the table's leading "Total Closed" column, and the "N of M closed" subtitle
    // denominator. Gate-free, so it's the right context (a pending/denied building can still be selected).
    Metrics.CLOSED_COUNT,
    Metrics.FACILITY_COUNT,
  ],
  notSelected: [
    Metrics.NOT_SELECTED_COUNT, // "Total Not Selected" column + parent/leaf counts
    Metrics.NOT_SELECTED_RATE, // "Not Selected (%)" column
    // closed_count = each entity's "Total Closed" (decided = selected + not_selected) — the leading
    // table column, the reason sub-row % denominator, and the tile subtitle. Gate-free. (The Least
    // Selected SNF tile reads selected_rate by facility from its own dashboard-scoped side query.)
    Metrics.CLOSED_COUNT,
    Metrics.FACILITY_COUNT,
  ],
  admitted: [
    Metrics.SELECTED_COUNT, // grey "Total Selected" bar + table column
    Metrics.ADMITTED_COUNT, // blue "Total Admitted" bar + table column
    Metrics.ADMISSION_RATE, // "% of Selected" tile + per-row "Selected to Admitted Conversion"
    // Header tiles only (not table columns): "% of Received" (+ referral:count for its "N of M
    // received" addendum) and "Awaiting Admission".
    Metrics.ADMISSION_RATE_OF_RECEIVED,
    Metrics.AWAITING_ADMISSION_COUNT,
    Metrics.REFERRAL_COUNT,
    Metrics.FACILITY_COUNT,
  ],
};

// Sub-division dimensions folded into a drill's breakdown query so its table rows expand into a
// reason breakdown. Denied expands by (category, specific reason) so each sub-row reads
// "Clinical - Trach New". The dashboard-wide donut/top-reason are derived from these same grouping-0
// leaves in DeniedDetailHeader (summed over the entity dim) — under keepPreviousData they keep their
// values on a View By switch, so no separate query is needed.
export const INBOUND_METRIC_DRILL_SUB_DIMENSIONS: Partial<Record<InboundMetricDrillKey, Dimensions[]>> = {
  denied: [Dimensions.DENIAL_REASON_CATEGORY, Dimensions.DENIAL_REASON_DESCRIPTION],
  // Not Selected expands by a single flat reason dim (unlike Denied's category→description nesting).
  notSelected: [Dimensions.NOT_SELECTED_REASON],
};

// Explicit CSV columns in on-screen (table) order. The drill query sets list the rate before the
// count, but every drill table (and its CSV) shows the count before the rate — so each built drill
// declares its column order here rather than relying on the query-set order (OLIO-3199). Unset drills
// fall back to their query set minus FACILITY_COUNT.
export const INBOUND_METRIC_DRILL_EXPORT_METRICS: Partial<Record<InboundMetricDrillKey, Metrics[]>> = {
  accepted: [
    Metrics.REFERRAL_COUNT,
    Metrics.ACCEPTED_COUNT,
    Metrics.ACCEPTANCE_RATE,
    Metrics.TIME_TO_ACCEPTED_DECISION_SECONDS_AVERAGE,
  ],
  denied: [
    Metrics.REFERRAL_COUNT,
    Metrics.DENIED_COUNT,
    Metrics.DENIAL_RATE,
    Metrics.TIME_TO_DENIAL_DECISION_SECONDS_AVERAGE,
  ],
  selected: [Metrics.CLOSED_COUNT, Metrics.SELECTED_COUNT, Metrics.SELECTED_RATE],
  notSelected: [Metrics.CLOSED_COUNT, Metrics.NOT_SELECTED_COUNT, Metrics.NOT_SELECTED_RATE],
  admitted: [Metrics.SELECTED_COUNT, Metrics.ADMITTED_COUNT, Metrics.ADMISSION_RATE],
};

// Fallbacks differ by purpose, not by oversight: the slideout query needs the determination-rate
// set (with rollups) so an unbuilt drill still renders a table; the export mirrors the rendered
// CSV columns. Both are only reached until a drill declares its own set above.
export function getInboundMetricSlideoutParams(
  metric: InboundMetricDrillKey,
  dimension: Dimensions
): InsightsQueryParams {
  const subDimensions = INBOUND_METRIC_DRILL_SUB_DIMENSIONS[metric] ?? [];
  return {
    source: Sources.REFERRAL_INTAKES,
    dimensions: [dimension, ...subDimensions],
    metrics: INBOUND_METRIC_DRILL_METRICS[metric] ?? INBOUND_ENTITY_SLIDEOUT_METRICS,
    rollups: true,
  };
}

// Export mirrors the metric drill's breakdown-table columns (no rollups — the CSV lists rows).
// FACILITY_COUNT is a subtitle-only metric (not a table column), so it's dropped from the export.
export function getInboundMetricExportParams(
  metric: InboundMetricDrillKey,
  dimension: Dimensions
): InsightsQueryParams {
  const metrics =
    INBOUND_METRIC_DRILL_EXPORT_METRICS[metric] ??
    (INBOUND_METRIC_DRILL_METRICS[metric] ?? INBOUND_TABLE_METRICS).filter((m) => m !== Metrics.FACILITY_COUNT);
  return {
    source: Sources.REFERRAL_INTAKES,
    dimensions: [dimension],
    metrics,
  };
}

const INBOUND_VIEW_BY_EXPORT_SLUG: Record<InboundViewByValue, string> = {
  receivingSnf: 'receiving-snf',
  referralSource: 'referral-source',
  payerSource: 'payer-source',
};

// Export mirrors the details table's rendered columns (no rollups — the CSV lists rows).
export function getInboundExportParams(dimension: Dimensions): InsightsQueryParams {
  return {
    source: Sources.REFERRAL_INTAKES,
    dimensions: [dimension],
    metrics: INBOUND_TABLE_METRICS,
  };
}

export function getInboundExportFilename(viewBy: InboundViewByValue): string {
  return `inbound-referral-details-by-${INBOUND_VIEW_BY_EXPORT_SLUG[viewBy]}`;
}

// ----- Drill-down slideouts (entity drill + metric drills) -----

// The three landing view-by dimensions, in display order. A slideout's dynamic View By offers the
// other two (entity drill) or all three (metric drill).
export const INBOUND_DASHBOARD_DIMENSIONS: Dimensions[] = Object.values(INBOUND_VIEW_BY_DIMENSION_MAP);

// Only the facility dimension is a discrete entity with an id column; referral_source/payer_source
// are workflow-value text that serve as their own filter value.
export const INBOUND_ENTITY_DIMENSIONS = new Set<Dimensions>([Dimensions.FACILITY]);

// Append `:with_id` to entity dimensions so the response carries the group id alongside the name —
// needed to scope the entity drill (facility filters on group_id).
export function withInboundDimensionIds(params: InsightsQueryParams): InsightsQueryParams {
  return {
    ...params,
    dimensions: params.dimensions?.map((d) =>
      INBOUND_ENTITY_DIMENSIONS.has(d as Dimensions) ? `${d}${WITH_ID_SUFFIX}` : d
    ),
  };
}

// Human label for a dimension when used as an entity-drill title / view-by chip.
export const INBOUND_DIMENSION_LABELS: Partial<Record<Dimensions, string>> = {
  [Dimensions.FACILITY]: 'Receiving SNF',
  [Dimensions.REFERRAL_SOURCE]: 'Referral Source',
  [Dimensions.PAYER_SOURCE]: 'Primary Payer',
};

// Entity-drill View By: the other two dashboard dimensions (inbound has no sub-division dim).
// First entry is the default selection.
export function getInboundSlideoutViewByOptions(clicked: Dimensions): Dimensions[] {
  return INBOUND_DASHBOARD_DIMENSIONS.filter((d) => d !== clicked);
}

export function getDefaultInboundSlideoutDimension(clicked: Dimensions): Dimensions {
  return getInboundSlideoutViewByOptions(clicked)[0] ?? clicked;
}

// Metric-drill View By offers all three dashboard dimensions, in order.
export const INBOUND_METRIC_DRILL_VIEW_BY_OPTIONS: Dimensions[] = INBOUND_DASHBOARD_DIMENSIONS;

// The rate tiles that open a metric drill. The store's InboundMetricDrillKey is derived from these keys.
export const INBOUND_METRIC_DRILL_TITLES = {
  accepted: 'Acceptance Rate \u2013 Overview',
  denied: 'Denial Rate \u2013 Overview',
  selected: 'Selected Rate \u2013 Overview',
  notSelected: 'Not Selected Rate \u2013 Overview',
  admitted: 'Admission Rate \u2013 Overview',
} as const;

export const INBOUND_METRIC_DRILL_EXPORT_SLUGS = {
  accepted: 'accepted-rate',
  denied: 'denied-rate',
  selected: 'selected-rate',
  notSelected: 'not-selected-rate',
  admitted: 'admission-rate',
} as const;

export const INBOUND_DIMENSION_EXPORT_SLUGS: Partial<Record<Dimensions, string>> = {
  [Dimensions.FACILITY]: 'receiving-snf',
  [Dimensions.REFERRAL_SOURCE]: 'referral-source',
  [Dimensions.PAYER_SOURCE]: 'payer-source',
};

// A dimension value that represents real data, not a NULL bucket. The analytics response formats a
// NULL dimension value to the literal string 'None', so both the empty and 'None' cases are dropped.
// Shared by the drill breakdown derivations (donut/top-reason/top-facility) so the "skip the NULL
// bucket" rule stays identical across them.
export function isRealDimensionValue(value: string | null | undefined): value is string {
  return !!value && value.toLowerCase() !== 'none';
}

// Slugifies an entity name for an export filename (lowercase, non-alphanumerics to dashes,
// trimmed). Shared by the slideout breakdown-table and patient-list exports.
export function slugify(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}
